import { useMemo } from "react"

import { formatNumber } from "~/ui-rtk/utils/format"

import type { LtvMetricMode } from "../constants"
import {
  weightedAverageAtOffset,
  type CohortGranularity,
  type CohortRow,
} from "../utils/cohort-matrix"

const CARD_OFFSETS = [3, 6, 12, 24]

export type HistoricalAveragesProps = {
  granularity: CohortGranularity
  cohortRows: CohortRow[]
  mode: LtvMetricMode
  todayIso: string
}

/**
 * "Avg N month/week" cards: customer-weighted accumulated value at fixed
 * offsets across every loaded cohort mature at that offset. Cards with no
 * mature cohort in the window are omitted.
 */
export default function HistoricalAverages({
  granularity,
  cohortRows,
  mode,
  todayIso,
}: HistoricalAveragesProps) {
  const periodNoun = granularity === "month" ? "month" : "week"

  const cards = useMemo(
    () =>
      CARD_OFFSETS.flatMap(offset => {
        const average = weightedAverageAtOffset(
          cohortRows,
          offset,
          mode,
          granularity,
          todayIso,
        )
        return average
          ? [{ offset, value: average.value, cohortCount: average.cohortCount }]
          : []
      }),
    [cohortRows, mode, granularity, todayIso],
  )

  if (cards.length === 0) return null

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map(card => (
        <div key={card.offset} className="bg-system-blue-800 rounded-lg p-6">
          <div className="text-sm text-gray-light-2">
            Avg {card.offset} {periodNoun}
          </div>
          <div className="text-2xl font-semibold mt-1">
            {formatNumber(card.value, { currency: true, decimals: 0 })}
          </div>
          <div className="text-xs text-gray-light-2 mt-1">
            {card.cohortCount} mature cohort{card.cohortCount === 1 ? "" : "s"}
          </div>
        </div>
      ))}
    </div>
  )
}
